import React , {ReactNode} from 'react';
import { GatsbyImage, getImage } from 'gatsby-plugin-image';
import { BLOCKS, MARKS,  Document, Block, Inline, INLINES} from "@contentful/rich-text-types"
import { documentToReactComponents, Options } from '@contentful/rich-text-react-renderer';
import { 
    Box, Container, Typography, Link as LinkMUI, Table, TableCell, TableContainer, TableRow, Paper, Divider, 
    List, ListItem, ListItemText
} from '@mui/material'; // Assuming you're using Material-UI
import ComponentContainer from './atoms/ComponentContainer';
import type { RichText as RichTextProps } from '../types/app';

interface Reference {
    contentful_id       : string;
    image               : any; // Assuming this is the correct type for Gatsby Image data
    title               : string;
    alt                 : string;
}
  
const richTextImages : {[key:string] : Reference} = {};

const Normal = ({ children,sx={}} : {children:ReactNode, sx?:object}) => (
    <Typography component="span" sx={{fontSize: 'inherit',...sx}}>
        {children}
    </Typography>
);

const Bold = ({ children } : {children:ReactNode}) => (
    <Typography component="span" sx={{fontWeight:'600!important', fontSize: 'inherit' }}>
        {children}
    </Typography>
);

const Italic = ({ children } : {children:ReactNode}) => (
    <Typography component="span" sx={{fontStyle: "italic", fontSize: 'inherit' }}>
        {children}
    </Typography>
);

const Underline = ({ children } : {children:ReactNode}) => (
    <Typography component="span" sx={{fontStyle: "underline", fontSize: 'inherit' }}>
        {children}
    </Typography>
);

const Code = ({ children } : {children:ReactNode}) => (
    <code>
        <Box sx={{mx:2, p:2, background:theme => theme.palette.background.paper, borderLeft:theme => `10px solid ${theme.palette.primary.main}`}}>
            <Typography component="div" style={{ fontFamily: 'monospace', fontSize: 'inherit' }}>
                {children}
            </Typography>
        </Box>
    </code>
);

const Quote= ({ children } : {children:ReactNode}) => (
    <Box sx={{mx:2, p:2, borderLeft: '10px solid #ddd'}}>
        <Typography component="div" style={{color: '#666', fontStyle: 'italic', fontSize: 'inherit' }}>
            {children}
        </Typography>
    </Box>
);

const Node = ({ children, sx = {}, ...props} : {children:ReactNode, sx ?:object, [key: string]: any}) => (
    <Typography component={Box} {...props} sx={{...sx,"& > * + *" : {mt:2}}} className=".node">
        {children}
    </Typography>
);

const Text = ({ children } : {children:ReactNode}) => (
    <>
        {children}
    </>
);

const options : Options = {
    renderMark: {
        [MARKS.BOLD]            : (text:ReactNode)  => <Bold>{text}</Bold>,
        [MARKS.ITALIC]          : (text:ReactNode)  => <Italic>{text}</Italic>,
        [MARKS.UNDERLINE]       : (text:ReactNode)  => <Underline>{text}</Underline>,
        [MARKS.CODE]            : (text:ReactNode)  => <Code> {text}</Code>,
        [MARKS.SUPERSCRIPT]     : (text: ReactNode) => <Normal sx={{ verticalAlign: 'super', fontSize: 'smaller' }}>{text}</Normal>,
        [MARKS.SUBSCRIPT]       : (text: ReactNode) => <Normal sx={{ verticalAlign: 'sub', fontSize: 'smaller' }}>{text}</Normal>,
        [MARKS.STRIKETHROUGH]   : (text: ReactNode) => <Normal sx={{ textDecoration: 'line-through' }}>{text}</Normal>,
    },
    renderNode: {
        [BLOCKS.PARAGRAPH]: (node: Block | Inline, children: ReactNode) => {
            const {
                textAlign       = "justify",
                fontSize        = "inherit",
                fontFamily      = "inherit",
                fontWeight      = "inherit",
                fontStyle       = "inherit",
                textDecoration  = "inherit",
                lineHeight      = "inherit",
                letterSpacing   = "inherit",
            } = node?.data?.target || {};
            const textStyles    = {textAlign, fontSize, fontFamily, fontWeight, fontStyle, textDecoration, lineHeight, letterSpacing };
            return (
                <Node sx={{ ...textStyles }}>
                    {children}
                </Node>
            );
        },
        [BLOCKS.EMBEDDED_ASSET]: (node: Block | Inline) => {
            try {
                const {image : rawImageData, title, alt} = richTextImages[node.data.target.sys.id];
                const image = getImage(rawImageData);
                if(!image)
                    throw new Error("image not found")
                return (
                    <Container maxWidth="sm">
                        <Box textAlign={"center"}>
                            <GatsbyImage image={image} alt={alt}/>
                            {
                                false && title && 
                                <Typography variant="body2">
                                    {title}
                                </Typography>
                            }
                        </Box>
                    </Container>
                );
            } catch (err) {
                //
            }
            return null;
        },
        [BLOCKS.HEADING_1]: (node: Block | Inline, children: ReactNode) => {
            return <Typography variant="h1" component="h1">{children}</Typography>;
        },
        [BLOCKS.HEADING_2]: (node: Block | Inline, children: ReactNode) => {
            return <Typography variant="h2" component="h2">{children}</Typography>;
        },
        [BLOCKS.HEADING_3]: (node: Block | Inline, children: ReactNode) => {
            return <Typography variant="h3" component="h3">{children}</Typography>;
        },
        [BLOCKS.HEADING_4]: (node: Block | Inline, children: ReactNode) => {
            return <Typography variant="h4" component="h4">{children}</Typography>;
        },
        [BLOCKS.HEADING_5]: (node: Block | Inline, children: ReactNode) => {
            return <Typography variant="h5" component="h5">{children}</Typography>;
        },
        [BLOCKS.HEADING_6]: (node: Block | Inline, children: ReactNode) => {
            return <Typography variant="h6" component="h6">{children}</Typography>;
        },
        [BLOCKS.QUOTE]: (node: Block | Inline, children: ReactNode) => <Quote> {children}</Quote>,
        [BLOCKS.TABLE]: (node: Block | Inline, children: ReactNode) => (
            <Container maxWidth={"md"}>
                <TableContainer component={Paper} sx={{ margin: '1.5em 0' }}>
                    <Table>
                        {children}
                    </Table>
                </TableContainer>
            </Container>
        ),
        [BLOCKS.TABLE_ROW]: (node: Block | Inline, children: ReactNode) => (
            <TableRow>
                {children}
            </TableRow>
        ),
        [BLOCKS.TABLE_CELL]: (node: Block | Inline, children: ReactNode) => (
            <TableCell variant="body">
                {children}
            </TableCell>
        ),
        [BLOCKS.TABLE_HEADER_CELL]: (node: Block | Inline, children: ReactNode) => (
            <TableCell variant="head" component="th">
                {children}
            </TableCell>
          ),
        [INLINES.HYPERLINK]: (node: Block | Inline, children: ReactNode) => {
            const { uri } = node.data;
            return (
                <LinkMUI href={uri} sx={{ color: 'primary.dark', textDecoration: 'underline' }}>
                    {children}
                </LinkMUI>
            );
        },
        [BLOCKS.HR]: (node: Block | Inline) => (
            <Divider sx={{ margin: '1.5em 0' }} />
        ),
        [BLOCKS.OL_LIST]: (node: Block | Inline, children: ReactNode) => (
            <List component="ol" sx={{padding: 0, paddingLeft: '1.5em', listStyleType: 'decimal'}}>
                {children}
            </List>
        ),
        [BLOCKS.UL_LIST]: (node: Block | Inline, children: ReactNode) => (
            <List component="ul" sx={{padding: 0, paddingLeft: '1.5em', listStyleType: 'disc' }}>
                {children}
            </List>
        ),
        [BLOCKS.LIST_ITEM]: (node: Block | Inline, children: ReactNode) => (
            <ListItem disableGutters sx={{ display: 'list-item' }}>
                <ListItemText primary={children} />
            </ListItem>
        ),
    },
    renderText: (text: string) => <Text>{text}</Text>
};

// Example usage with documentToReactComponents
const RichTextRenderer: React.FC<{ document: Document }> = ({ document }) => (
    <>
        { 
            documentToReactComponents(document, options) 
        }
    </>
);

export const RichText = (props: RichTextProps) => {
    
    const document = JSON.parse(props.body.raw) as Document;
    
    // Store the image data
    (props?.body?.references || []).forEach(reference => {
        if(reference?.__typename === "ContentfulAsset"){
            richTextImages[reference.contentful_id] = {
                contentful_id   : reference.contentful_id, 
                image           : reference.gatsbyImageData, 
                title           : reference.title, 
                alt             : reference.title
            }
        }
    })
    
    return (
        <ComponentContainer contentful_id={props.contentful_id} data-sb-field-path="body">
            <Box id="richTextRenderContainer" sx={{mb:2, "& > * + *" : { mt: theme => `${theme.spacing(2)}!important` }}}>
                <RichTextRenderer document={document} />
            </Box>
            {
                false && 
                JSON.stringify(props.body.references)
            }
        </ComponentContainer>
    )
};

export default RichText;
